import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useT } from '@/contexts/I18nContext';
import { spliceLine, mskHhmmToMs, msToMskHhmm, type KeypointBlockLine } from '../constructor/lineModel';
import {
  serializeKeypointBlock,
  classifyKeypointRow,
  getFilledAt,
  getReplenishedAt,
  hasFilledMarker,
  hasReplenishedMarker,
  type KeypointBlock,
  type KeypointBlockOld,
  type KeypointBlockNew,
} from '../constructor/keypointBlock';
import { RackChart, useRackLayoutDetail } from './RackChart';
import type { ChartKeypoint } from '../chart/types';
import { Tooltip } from '@/components/ui/tooltip';

// `format` deliberately excluded — a block never switches format via a patch,
// only fields within whichever one it was parsed as. See KeypointBlockForm.tsx
// for the same pattern.
type KeypointPatch = Partial<Omit<KeypointBlockOld, 'format'>> & Partial<Omit<KeypointBlockNew, 'format'>>;

type KpId = 'open' | 'norm' | 'decline' | 'close';
const KP_LABEL_KEY: Record<KpId, string> = {
  open: 'reportValidation.tf.kp_open',
  norm: 'reportValidation.tf.kp_norm',
  decline: 'reportValidation.tf.kp_decline',
  close: 'reportValidation.tf.kp_close',
};
// items 16-19: each report line gets ITS OWN colour, matched to its chart
// pin — previously every pin was the same accent colour and the 3-4 lines
// of a block rendered as one undifferentiated paragraph, so there was no
// way to tell which marker belonged to which sentence.
const KP_COLOR: Record<KpId, string> = {
  open: '#f59e0b', // amber
  norm: '#22c55e', // green
  decline: '#ef4444', // red
  close: '#6366f1', // indigo
};

/**
 * F2 — ключевые точки block: the SAME per-rack charts as F1 (fresh instances,
 * own pan/zoom), this time in keypoint mode. Keypoints are ONE set synced
 * across every rack of the type (Alexey's spec — "not each annotated
 * separately, but the contrary"), so state lives here once and is handed
 * identically to every `RackChart`.
 */
export function KeypointChartBlock({
  line,
  racks,
  zoneId,
  day,
  value,
  onChange,
  disabled,
  spanRows,
}: {
  line: KeypointBlockLine;
  racks: { id: number; name: string }[];
  zoneId: string;
  day: string;
  value: string;
  onChange: (bodyHtml: string) => void;
  disabled?: boolean;
  /** items 16-19: the block's own raw HTML rows (one per report line), so
   *  each can render as its own colour-linked mini-card above the shared
   *  chart(s) instead of InteractiveView dumping them as one paragraph. */
  spanRows?: string[];
}) {
  const { t } = useT();
  const kb = line.block;
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Close time isn't in KeypointBlock (only open is, for the "after open" calc) —
  // read it off the same layout-detail request the first rack's chart already
  // makes (SWR dedupes: no extra request).
  const { data: layout } = useRackLayoutDetail(zoneId, day, racks[0]?.id);
  const closeHhmm = useMemo(() => {
    const end = layout?.working_hours?.end;
    // item 10c: closing anchors 20 min before the literal close instant
    // everywhere in the system (mirrors CLOSING_SNAPSHOT_LEAD_MINUTES in
    // empty_shelf.py) — staff often already clear stock right at close.
    return end ? msToMskHhmm(new Date(end).getTime() - 20 * 60_000) : null;
  }, [layout?.working_hours?.end]);

  const openMs = kb.openTime ? mskHhmmToMs(day, kb.openTime) : null;
  const closeMs = closeHhmm ? mskHhmmToMs(day, closeHhmm) : null;
  // "norm"/"decline" pin ids are kept from the OLD naming for both formats —
  // conceptually they're the same two extra events either way: the moment
  // the shelf reached normal fill (old: normalizedTime, new: filledAt) and
  // the moment of the last replenishment before close (old: declineTime,
  // new: replenishedAt). getFilledAt/getReplenishedAt/hasFilledMarker/
  // hasReplenishedMarker bridge the two formats' different field names.
  const filledAtHhmm = getFilledAt(kb);
  const replenishedAtHhmm = getReplenishedAt(kb);
  const normMs = filledAtHhmm ? mskHhmmToMs(day, filledAtHhmm) : null;
  const declineMs = replenishedAtHhmm ? mskHhmmToMs(day, replenishedAtHhmm) : null;
  const filledMarkerPresent = hasFilledMarker(kb);
  const replenishedMarkerPresent = hasReplenishedMarker(kb);

  const push = (patch: KeypointPatch) =>
    onChange(spliceLine(value, line, serializeKeypointBlock({ ...kb, ...patch } as KeypointBlock)));

  // item 10d: whether the "пришло в норму к" marker applies is BACKEND-driven
  // — mirrors empty_shelf.py's `clarify_category` rule (reached the normal
  // fill level within 20 minutes of opening ⇒ no separate marker), but reads
  // the rack's OWN live fill-series (already fetched for the chart) against
  // the validator's currently-edited opening% instead of the backend's
  // per-shelf empty_shelf_score threshold (not available client-side) — so
  // it reacts live as the opening slider moves, same underlying 20-minute
  // constant the backend uses. Replaces the old "+ Выход на норму" manual
  // add-chip: the marker now appears/disappears on its own.
  const fillPoints = useMemo(() => {
    const ts = layout?.shelf_timeseries;
    if (!ts) return [] as { t: number; v: number }[];
    return ts.points.map((p) => {
      const vals = Object.values(p.values).filter((v): v is number => v != null);
      const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
      return { t: new Date(p.date).getTime(), v: avg };
    });
  }, [layout?.shelf_timeseries]);

  // Only mirror the backend condition AFTER the validator actually touches
  // the opening-% slider — Alexey's spec is "ЕСЛИ [validator] указали
  // заполненность на открытии < X" (a reaction to an EDIT), not a silent
  // pre-emptive rewrite of whatever the server originally decided. This also
  // sidesteps a real footgun: this client-side re-derivation uses the AVERAGE
  // fill across shelves (fillPoints) against the reparsed openingPct number,
  // which won't always exactly reproduce the backend's own per-shelf
  // empty_shelf_score-threshold math — auto-firing on mount could flip a
  // perfectly correct server verdict the moment the block loads.
  const openingPctTouchedRef = useRef(false);
  useEffect(() => {
    if (!openingPctTouchedRef.current) return;
    if (openMs == null || !fillPoints.length) return;
    const hit = fillPoints.find((p) => p.t >= openMs && p.v >= kb.openingPct);
    const minsToNormal = hit ? Math.round((hit.t - openMs) / 60_000) : null;
    const shouldBeFast = minsToNormal != null && minsToNormal <= 20;
    if (shouldBeFast && filledMarkerPresent) {
      if (kb.format === 'old') push({ openingFast: true });
      else push({ openingProblem: false, filledAt: null });
    } else if (!shouldBeFast && !filledMarkerPresent) {
      const time = hit ? msToMskHhmm(hit.t) : kb.openTime ?? '12:00';
      if (kb.format === 'old') push({ openingFast: false, normalizedTime: kb.normalizedTime ?? time });
      else push({ openingProblem: true, filledAt: kb.filledAt ?? time });
    }
    // deliberately depends only on inputs that can change the verdict
    // (openMs/fillPoints/openingPct); `push`/kb.* excluded on purpose to
    // avoid a self-triggering loop.
  }, [openMs, kb.openingPct, fillPoints]);

  const keypoints: ChartKeypoint[] = useMemo(() => {
    const out: ChartKeypoint[] = [];
    // item 10a/b: open/close are ALWAYS fixed (never draggable) — square
    // marker, "not draggable" fast tooltip, plus a level tick (item 10e) at
    // the currently-selected %.
    if (openMs != null)
      out.push({
        id: 'open',
        t: openMs,
        color: KP_COLOR.open,
        fixed: true,
        fixedTooltip: 'Открытие — время фиксировано, двигать нельзя',
        level: kb.openingPct,
      });
    if (filledMarkerPresent && normMs != null) out.push({ id: 'norm', t: normMs, color: KP_COLOR.norm });
    if (replenishedMarkerPresent && declineMs != null) out.push({ id: 'decline', t: declineMs, color: KP_COLOR.decline });
    if (closeMs != null)
      out.push({
        id: 'close',
        t: closeMs,
        color: KP_COLOR.close,
        fixed: true,
        fixedTooltip: 'Закрытие (за 20 мин. до факт. закрытия) — время фиксировано, двигать нельзя',
        level: kb.closingPct,
      });
    return out;
  }, [openMs, closeMs, normMs, declineMs, filledMarkerPresent, replenishedMarkerPresent, kb.openingPct, kb.closingPct]);

  const handleKeypointChange = (kp: ChartKeypoint, _v: number | null) => {
    const id = kp.id as KpId;
    // open/close are `fixed` — the chart itself never fires a drag for them,
    // this is just defensive.
    if (id === 'norm') {
      if (kb.format === 'old') push({ normalizedTime: msToMskHhmm(kp.t) });
      else push({ filledAt: msToMskHhmm(kp.t) });
    } else if (id === 'decline') {
      if (kb.format === 'old') push({ declineTime: msToMskHhmm(kp.t) });
      else push({ replenishedAt: msToMskHhmm(kp.t) });
    }
  };

  // item 10d: "norm" is no longer a manually addable chip — its presence
  // mirrors the backend condition automatically (see the effect above).
  const missing: KpId[] = [...(!replenishedMarkerPresent ? (['decline'] as KpId[]) : [])];

  const sel = keypoints.find((k) => k.id === selectedId) ?? null;
  // item 8: the % sliders keep a LOCAL override while a keypoint stays
  // selected, instead of reading `kb.openingPct`/`closingPct` straight off
  // the parsed body_html on every render. Below 15% those percentages
  // serialize to a wordy "почти не было на прилавке" with NO number (same
  // phrasing the backend itself emits) — re-parsing that text on the next
  // render seeds a fixed fallback (10), so without this override the slider
  // visibly "bounced back" and could never actually settle at (or reach) 0.
  const [pctOverride, setPctOverride] = useState<{ id: string; value: number } | null>(null);
  useEffect(() => {
    setPctOverride(null);
  }, [selectedId]);

  // item B: fill_on_open/fill_on_close are point-in-time LEVELS — show the
  // actual photo from that moment beside a 5%-step slider pre-set to the
  // currently recorded value, so a validator can see the shelf and just
  // nudge the number if the auto-read level looks off, instead of typing a
  // blind percentage. Representative photo = the first rack's (racks[0],
  // same one closeHhmm already reads working_hours off — no extra request).
  const nearestPhotoUrl = useMemo(() => {
    if (!sel || (sel.id !== 'open' && sel.id !== 'close')) return null;
    const photos = (layout?.rack_photos ?? []).filter((p) => p.image_url);
    if (!photos.length) return null;
    let best = photos[0];
    let bestD = Infinity;
    for (const p of photos) {
      const d = Math.abs(new Date(p.at).getTime() - sel.t);
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    return best.image_url;
  }, [sel, layout?.rack_photos]);

  const readout = (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/30 p-2 text-xs">
      {sel ? (
        <>
          <span className="font-medium">{t(KP_LABEL_KEY[sel.id as KpId])}</span>
          <span className="text-muted-foreground">{msToMskHhmm(sel.t)} МСК</span>
          {(sel.id === 'open' || sel.id === 'close') && (() => {
            const kbPct = sel.id === 'open' ? kb.openingPct : kb.closingPct;
            const shownPct = pctOverride && pctOverride.id === sel.id ? pctOverride.value : kbPct;
            return (
              <div className="flex w-full items-center gap-3 pt-1">
                {/* item 8: `object-contain` (was `object-cover` in a narrow
                    fixed box) — a wide panorama shelf crop no longer gets
                    its sides sliced off; letterboxes instead of cropping. */}
                {nearestPhotoUrl && (
                  <img
                    key={`${sel.id}-${nearestPhotoUrl}`}
                    src={nearestPhotoUrl}
                    alt=""
                    className="h-16 w-32 shrink-0 rounded-md border border-border bg-muted object-contain"
                  />
                )}
                <div className="flex flex-1 items-center gap-2">
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    disabled={disabled}
                    value={Math.round(shownPct / 5) * 5}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setPctOverride({ id: sel.id, value: v });
                      if (sel.id === 'open') {
                        openingPctTouchedRef.current = true; // item 10d: only mirror-check after a real edit
                        push({ openingPct: v });
                      } else {
                        push({ closingPct: v });
                      }
                    }}
                    className="flex-1 accent-primary"
                  />
                  <span className="w-10 shrink-0 text-right font-medium text-foreground">
                    {Math.round(shownPct)}%
                  </span>
                </div>
              </div>
            );
          })()}
          {sel.id === 'decline' && (
            <Tooltip content={t('reportValidation.interactive.removeKeypoint')}><button
              type="button"
              disabled={disabled}
              onClick={() => {
                if (kb.format === 'old') push({ declinePresent: false });
                else push({ closingProblem: false, replenishedAt: null });
                setSelectedId(null);
              }}
              className="ml-auto rounded-md border border-border p-1 text-destructive hover:bg-destructive/10"
              aria-label={t('reportValidation.interactive.removeKeypoint')}
            >
              <Trash2 className="size-3.5" />
            </button></Tooltip>
          )}
        </>
      ) : (
        <>
          {missing.map((id) => (
            <button
              key={id}
              type="button"
              disabled={disabled}
              onClick={() => {
                const t = getFilledAt(kb) ?? kb.openTime ?? '12:00';
                if (kb.format === 'old') push({ declinePresent: true, declineTime: t });
                else push({ closingProblem: true, replenishedAt: t });
              }}
              className={cn(
                'inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-0.5',
                'text-muted-foreground hover:border-primary hover:text-primary',
              )}
            >
              <Plus className="size-3" /> {t(KP_LABEL_KEY[id])}
            </button>
          ))}
        </>
      )}
    </div>
  );

  // items 16-19: each report LINE (open/norm/decline/close sentence) gets
  // its own small colour-linked card, so it's visually obvious which chart
  // pin it corresponds to — clicking one selects that keypoint on every
  // rack chart below. Replaces the single merged paragraph InteractiveView
  // used to render for the whole block.
  const lineCards = (spanRows ?? [])
    .map((row, i) => ({ row, kind: classifyKeypointRow(row), i }))
    .filter((r): r is { row: string; kind: KpId; i: number } => r.kind != null);

  return (
    <div className="flex flex-col gap-3">
      {lineCards.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {lineCards.map(({ row, kind, i }) => {
            const kp = keypoints.find((k) => k.id === kind);
            const active = selectedId === kind;
            return (
              <button
                key={i}
                type="button"
                onClick={() => kp && setSelectedId(active ? null : kind)}
                disabled={!kp}
                className={cn(
                  'rounded-md border py-1.5 pl-2.5 pr-3 text-left text-sm leading-snug transition',
                  active ? 'border-current bg-muted/40' : 'border-border hover:bg-muted/20',
                  !kp && 'cursor-default opacity-70',
                )}
                style={{ borderLeftColor: KP_COLOR[kind], borderLeftWidth: 3, color: active ? KP_COLOR[kind] : undefined }}
              >
                <span className="text-foreground" dangerouslySetInnerHTML={{ __html: row }} />
              </button>
            );
          })}
        </div>
      )}
      {/* item 2: each rack gets its OWN full width — a 2-up grid squeezed two
          charts (and their ≥10-photo strips) into half-width each. */}
      {racks.map((rack) => (
        <RackChart
          key={rack.id}
          zoneId={zoneId}
          day={day}
          rackId={rack.id}
          rackName={rack.name}
          annotateCreates="none"
          keypoints={keypoints}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onKeypointChange={handleKeypointChange}
          readout={readout}
        />
      ))}
    </div>
  );
}
